import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Link } from '@react-pdf/renderer';
import DraftTextRenderer from './DraftTextRenderer';

const styles = StyleSheet.create({
  page: { padding: 30, fontFamily: 'Helvetica', fontSize: 10 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#dddddd', paddingBottom: 10, marginBottom: 10 },
  logo: { maxWidth: 120, maxHeight: 60 },
  title: { fontSize: 24, fontWeight: 'bold' },
  sectionTitle: { fontSize: 14, fontWeight: 'bold', marginTop: 15, marginBottom: 5, paddingVertical: 4, paddingHorizontal: 6, backgroundColor: '#f0f0f0', borderTopWidth: 1, borderTopColor: '#cccccc', borderBottomWidth: 1, borderBottomColor: '#cccccc' },
  card: { padding: 10, marginBottom: 10, borderWidth: 1, borderColor: '#eeeeee', backgroundColor: '#fafafa', borderRadius: 4 },
  row: { flexDirection: 'row', width: '100%', paddingVertical: 3, borderBottomWidth: 1, borderBottomColor: '#f5f5f5' },
  colLabel: { width: '30%', fontSize: 9, fontWeight: 'bold', color: '#555555' },
  colValue: { width: '70%', fontSize: 9 },
  summaryText: { fontSize: 10, lineHeight: 1.4, marginTop: 5, paddingHorizontal: 6 },
  attachmentHeader: { position: 'absolute', top: 15, left: 30, right: 30, fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#666666' }
});

const Field = ({ label, value }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <View style={styles.row}>
      <Text style={styles.colLabel}>{label}</Text>
      <Text style={styles.colValue}>{value}</Text>
    </View>
  );
};

const AttachmentsField = ({ attachments }) => {
  if (!attachments) return null;
  return (
    <View style={styles.row}>
      <Text style={styles.colLabel}>Attachments</Text>
      <View style={styles.colValue}>
        <Text>{attachments}</Text>
        <Text style={{ fontSize: 8, fontStyle: 'italic', color: '#888888', marginTop: 2 }}>
          (Attachments are printed in the later attachment section)
        </Text>
      </View>
    </View>
  );
};

const IncidentPdfDocument = ({ incident, draftIncident, logoUrl, textContents = {}, loadingTexts = false }) => {
  if (!incident || loadingTexts) {
    return (
      <Document>
        <Page size="LETTER" style={[styles.page, { justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={{ fontSize: 18, color: '#666' }}>Fetching file attachments...</Text>
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      <Page size="LETTER" style={styles.page} wrap={true}>
        <View style={styles.header}>
          <Text style={styles.title}>Incident #{incident.number || ''}</Text>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
        </View>

        {/* General Details */}
        <View>
          <Text style={styles.sectionTitle}>General Information</Text>
          <Field label="Status" value={incident.status} />
          <Field label="Reported Date" value={incident.reportedDate} />
          <Field label="Occurred Date" value={incident.occurredDate} />
          <Field label="Location" value={incident.location || incident.address} />
          <Field label="What Happened" value={incident.whatHappened} />
          <Field label="Primary Cause" value={incident.primaryCause} />
          <Field label="Severity" value={incident.severity} />
          <Field label="Entered By" value={incident.createUserName} />
          <Field label="Assigned To" value={incident.assignedUserName} />
          <Text style={[styles.colLabel, { marginTop: 10, marginLeft: 6 }]}>Summary / What Happened:</Text>
          <DraftTextRenderer rawContent={incident.rawSummary} />
          {incident.causeComments ? (
            <>
              <Text style={[styles.colLabel, { marginTop: 10, marginLeft: 6 }]}>Cause Comments:</Text>
              <DraftTextRenderer rawContent={incident.rawCauseComments} />
            </>
          ) : null}
        </View>

        {/* Persons */}
        {incident.persons && incident.persons.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Involved Persons</Text>
            {incident.persons.map((p, i) => (
              <View style={styles.card} key={i}>
                <View style={{ flexDirection: 'row', width: '100%' }}>
                  <View style={{ width: p.photo ? '80%' : '100%', paddingRight: p.photo ? 15 : 0 }}>
                  <Field label="Name" value={p.displayName} />
                  <Field label="Type" value={p.personType} />
                  <Field label="Incident Relation" value={p.incidentRelation} />
                  <Field label="Status" value={p.status} />
                  <Field label="Gender" value={p.genderType} />
                  <Field label="Department" value={p.department} />
                  <Field label="Position" value={p.position} />
                  <Field label="Address" value={p.address} />
                  <Field label="Phone" value={p.phone} />
                  <Field label="Mobile Phone" value={p.mobilePhone} />
                  <Field label="Email" value={p.email} />
                  <Field label="Date of Birth" value={p.dateOfBirth} />
                  <Field label="Characteristics" value={p.characteristics} />
                  {p.rawComments ? (
                    <View style={styles.row}>
                      <Text style={styles.colLabel}>Comments</Text>
                      <View style={styles.colValue}>
                        <DraftTextRenderer rawContent={p.rawComments} />
                      </View>
                    </View>
                  ) : null}
                </View>
                  {p.photo && (
                    <View style={{ width: '20%', alignItems: 'center' }}>
                      <Image src={p.photo} style={{ width: 80, height: 80, borderRadius: 4, objectFit: 'cover' }} />
                    </View>
                  )}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Companies */}
        {incident.companies && incident.companies.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Involved Companies</Text>
            {incident.companies.map((c, i) => (
              <View style={styles.card} key={i}>
                <Field label="Company Name" value={c.name} />
                <Field label="Incident Relation" value={c.incidentRelation} />
                <Field label="Contact" value={c.contact} />
                <Field label="Address" value={c.address} />
                <Field label="Phone" value={c.phone} />
                <Field label="Email" value={c.email} />
                {c.rawComments ? (
                  <View style={styles.row}>
                    <Text style={styles.colLabel}>Comments</Text>
                    <View style={styles.colValue}>
                      <DraftTextRenderer rawContent={c.rawComments} />
                    </View>
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        )}

        {/* Vehicles */}
        {incident.vehicles && incident.vehicles.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Vehicles</Text>
            {incident.vehicles.map((v, i) => (
              <View style={styles.card} key={i}>
                <Field label="Owner" value={v.ownerName} />
                <Field label="Involvement" value={v.involvement} />
                <Field label="License Plate" value={v.licensePlate} />
                <Field label="State/Province" value={v.stateProvince} />
                <Field label="Make/Model" value={v.displayName} />
                <Field label="Color" value={v.color} />
                <Field label="VIN" value={v.vin} />
                {v.rawComments ? (
                  <View style={styles.row}>
                    <Text style={styles.colLabel}>Comments</Text>
                    <View style={styles.colValue}>
                      <DraftTextRenderer rawContent={v.rawComments} />
                    </View>
                  </View>
                ) : null}
                <AttachmentsField attachments={v.attachments} />
              </View>
            ))}
          </View>
        )}

        {/* Items */}
        {incident.items && incident.items.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Items</Text>
            {incident.items.map((it, i) => (
              <View style={styles.card} key={i}>
                <Field label="Item Name" value={it.name} />
                <Field label="Owner" value={it.ownerName} />
                <Field label="Status" value={it.status} />
                <Field label="Brand" value={it.brand} />
                <Field label="Model" value={it.model} />
                <Field label="Color" value={it.color} />
                <Field label="Serial Number" value={it.serialNumber} />
                <Field label="Value" value={it.value} />
                {it.rawComments ? (
                  <View style={styles.row}>
                    <Text style={styles.colLabel}>Comments</Text>
                    <View style={styles.colValue}>
                      <DraftTextRenderer rawContent={it.rawComments} />
                    </View>
                  </View>
                ) : null}
                <AttachmentsField attachments={it.attachments} />
              </View>
            ))}
          </View>
        )}

        {/* Corrective Actions */}
        {incident.correctiveActions && incident.correctiveActions.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Corrective Actions</Text>
            {incident.correctiveActions.map((ca, i) => (
              <View style={styles.card} key={i}>
                <Field label="Type" value={ca.type} />
                <Field label="Referred To" value={ca.referredTypeName} />
                <Field label="Contact First Name" value={ca.referredContactFirstName} />
                <Field label="Contact Last Name" value={ca.referredContactLastName} />
                <Field label="Contact Email" value={ca.referredContactEmail} />
                <Field label="Contact Phone" value={ca.referredContactPhone} />
                <Field label="Involved Persons" value={ca.involvedPersons} />
                {ca.rawDetails ? (
                  <View style={styles.row}>
                    <Text style={styles.colLabel}>Details</Text>
                    <View style={styles.colValue}>
                      <DraftTextRenderer rawContent={ca.rawDetails} />
                    </View>
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        )}

        {/* Attachments */}
        {incident.attachments && incident.attachments.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Attachments</Text>
            {incident.attachments.map((a, i) => (
              <View style={styles.card} key={i}>
                <View style={styles.row}>
                  <Text style={styles.colLabel}>File Name</Text>
                  <Text style={styles.colValue}>
                    {a.downloadUrl ? (
                      <Link src={a.downloadUrl}>{a.name}</Link>
                    ) : (
                      a.name
                    )}
                  </Text>
                </View>
                <Field label="Type" value={a.attachmentType} />
                {(() => {
                  const isText = a.fileType === 'text/plain' || (a.fileName && a.fileName.toLowerCase().endsWith('.txt'));
                  const isPdf = a.fileType === 'application/pdf' || (a.fileName && a.fileName.toLowerCase().endsWith('.pdf'));
                  const isOffice = a.fileName && /\.(doc|docx|xls|xlsx|ppt|pptx|rtf)$/i.test(a.fileName);
                  const isImage = !!a.photo;
                  const isSupported = isText || isPdf || isImage || isOffice;
                  return (
                    <View style={styles.row}>
                      <Text style={styles.colLabel}>Print Status</Text>
                      <Text style={[styles.colValue, !isSupported && { color: '#d32f2f', fontWeight: 'bold' }]}>
                        {isSupported ? 'Document Appended to Report' : 'Printing not supported. Click the link to download this attachment and print it separately.'}
                      </Text>
                    </View>
                  );
                })()}
                {a.rawComments ? (
                  <View style={styles.row}>
                    <Text style={styles.colLabel}>Comments</Text>
                    <View style={styles.colValue}>
                      <DraftTextRenderer rawContent={a.rawComments} />
                    </View>
                  </View>
                ) : null}
              </View>
            ))}
          </View>
        )}

        {/* Close Details */}
        {incident.close && (
          <View>
            <Text style={styles.sectionTitle}>Close Details</Text>
            <Field label="Closed By" value={incident.close.closeUserName} />
            <Field label="First Name" value={incident.close.closeFirstName} />
            <Field label="Last Name" value={incident.close.closeLastName} />
            <Field label="Department" value={incident.close.closeDepartment} />
            <Field label="Position" value={incident.close.closePosition} />
            <Field label="Email" value={incident.close.closeEmail} />
            <Field label="Phone" value={incident.close.closePhone} />
            <Text style={[styles.colLabel, { marginTop: 10, marginLeft: 6 }]}>Close Comments:</Text>
            <DraftTextRenderer rawContent={incident.close.rawCloseComments} />
          </View>
        )}
      </Page>

      {/* Automatically Generated End-Pages for Image and Text Attachments */}
      {incident.attachments && incident.attachments.map((a, i) => {
        if (a.photo) {
          return (
            <Page size="LETTER" style={styles.page} key={`att-img-${i}`}>
              <Text style={styles.attachmentHeader} fixed>Incident #{incident.number || ''} Attachment: {a.name}</Text>
              <View style={{ marginTop: 10, flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Image src={a.downloadUrl || a.photo} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </View>
            </Page>
          );
        }

        const txt = textContents[a.id || a.fileName || a.name];
        if (txt) {
          return (
            <Page size="LETTER" style={styles.page} key={`att-txt-${i}`}>
              <Text style={styles.attachmentHeader} fixed>Incident #{incident.number || ''} Attachment: {a.name}</Text>
              <View style={{ marginTop: 10, padding: 15, backgroundColor: '#f9f9f9', borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 4, flex: 1 }}>
                <Text style={{ fontFamily: 'Courier', fontSize: 10 }}>{txt}</Text>
              </View>
            </Page>
          );
        }

        return null;
      })}
    </Document>
  );
};

export default IncidentPdfDocument;
